import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Stack } from '@astryxdesign/core/Stack'
import { Card } from '@astryxdesign/core/Card'
import { Text } from '@astryxdesign/core/Text'
import { Badge } from '@astryxdesign/core/Badge'
import { Button } from '@astryxdesign/core/Button'
import { Banner } from '@astryxdesign/core/Banner'
import { TextInput } from '@astryxdesign/core/TextInput'
import { FileInput } from '@astryxdesign/core/FileInput'
import { Selector } from '@astryxdesign/core/Selector'
import { MultiSelector } from '@astryxdesign/core/MultiSelector'
import { ProgressBar } from '@astryxdesign/core/ProgressBar'
import { useMe, useTrip, useUsers } from '#/lib/hooks'
import { DOCUMENT_KINDS, isAdmin, type DocumentKind } from '#/lib/types'

export const Route = createFileRoute('/manage/upload')({ component: Upload })

/*
  Adding a paper, as a deliberate thing rather than a side effect.

  The old form lived at the top of the vault and fired the upload the instant a
  file was chosen — before you had said what it was, whose it was or when it
  expired, and with nothing on screen but the word "Uploading…". If it failed
  you got one sentence and your file selection was gone.

  This asks for the file first, shows you what you picked, then takes the
  details, then uploads on a press you meant. Nothing leaves the phone until
  that press.

  XMLHttpRequest rather than fetch, for one reason: fetch cannot report upload
  progress. A passport scan on hotel wifi is thirty seconds of silence
  otherwise, and silence is indistinguishable from broken.
*/

const MAX_BYTES = 25 * 1024 * 1024

function Upload() {
  const trip = useTrip()
  const me = useMe()
  const users = useUsers()
  const navigate = useNavigate()

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState<DocumentKind>('passport')
  const [belongsTo, setBelongsTo] = useState<string[]>([])
  const [expires, setExpires] = useState('')
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A thumbnail of what you actually picked, so "Papa — passport" cannot end
  // up attached to a photo of a hotel receipt.
  useEffect(() => {
    if (!file || !file.type.startsWith('image/')) { setPreview(null); return }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  if (me && !isAdmin(me.role)) return <Text type="supporting">Admins only.</Text>

  const scope = DOCUMENT_KINDS.find((d) => d.id === kind)?.scope ?? 'trip'
  const tooBig = !!file && file.size > MAX_BYTES
  const ready = !!file && !tooBig && title.trim().length > 0 && progress === null

  async function choose(files: File | File[] | null) {
    const f = Array.isArray(files) ? files[0] : files
    setError(null)
    setFile(f ?? null)
    // The filename is a decent first guess and a terrible final answer, so it
    // is offered as the value rather than silently used.
    if (f && !title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''))
  }

  function upload() {
    if (!file || !ready) return
    setError(null)
    setProgress(0)

    const form = new FormData()
    form.set('file', file)
    form.set('title', title.trim())
    form.set('kind', kind)
    form.set('scope', scope)
    if (scope === 'trip' && trip) form.set('tripId', trip.id)
    for (const id of belongsTo) form.append('ownerUserIds', id)
    if (expires) form.set('expiresOn', expires)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', '/api/documents')
    xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest')

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100))
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        void navigate({ to: '/manage/documents' })
        return
      }
      setProgress(null)
      setError(
        xhr.status === 413 ? 'That file is larger than 25 MB.'
        : xhr.status === 503 ? 'Document storage is not set up on this deployment yet.'
        : xhr.status === 403 ? 'Only an admin can upload documents.'
        : 'The upload failed. Your file is still selected — try again.',
      )
    }
    xhr.onerror = () => {
      setProgress(null)
      setError('The connection dropped. Your file is still selected — try again.')
    }
    xhr.send(form)
  }

  return (
    <Stack direction="vertical" gap={4}>
      <Stack direction="vertical" gap={0}>
        <Text type="display-3" display="block">Add a document</Text>
        <Text type="supporting">
          Stored privately and served only to signed-in members. Never put a
          passport number in the title — the scan already has it.
        </Text>
      </Stack>

      {error && <Banner status="error" title={error} collapsible={false} />}

      <Card>
        <Stack direction="vertical" gap={3}>
          <Text type="label" color="secondary">1 · The file</Text>
          <FileInput
            label="Choose a scan or PDF"
            value={null}
            accept="image/*,.pdf"
            maxSize={MAX_BYTES}
            onChange={() => {}}
            changeAction={choose}
          />
          {file && (
            <Stack direction="horizontal" gap={3} align="center">
              {preview
                ? <img className="upload-thumb" src={preview} alt="" />
                : <div className="upload-thumb upload-thumb-pdf"><Text type="large">PDF</Text></div>}
              <Stack direction="vertical" gap={1}>
                <Text weight="medium" maxLines={2}>{file.name}</Text>
                <Stack direction="horizontal" gap={2} align="center" wrap="wrap">
                  <Text type="supporting">
                    {file.size < 1024 ? '<1 KB' : `${Math.round(file.size / 1024)} KB`}
                  </Text>
                  {tooBig && <Badge label="Too large" variant="red" />}
                </Stack>
              </Stack>
            </Stack>
          )}
          {tooBig && (
            <Text type="supporting">
              The limit is 25 MB. A photo of a page is usually well under it;
              a scan at 600 dpi is usually well over.
            </Text>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack direction="vertical" gap={3}>
          <Text type="label" color="secondary">2 · What it is</Text>
          <TextInput
            label="Title"
            value={title}
            onChange={setTitle}
            placeholder="Papa — passport"
            isRequired
          />
          <Selector
            label="Kind"
            value={kind}
            onChange={(v) => setKind(v as DocumentKind)}
            presentation="adaptive"
            options={DOCUMENT_KINDS.map((d) => ({
              value: d.id,
              label: d.label,
              description: d.scope === 'global' ? 'Kept across all trips' : 'This trip only',
            }))}
          />
          <MultiSelector
            label="Belongs to"
            placeholder="Everyone"
            description="Leave empty if it covers the whole party."
            triggerDisplay="labels"
            presentation="adaptive"
            value={belongsTo}
            onChange={setBelongsTo}
            options={users
              .filter((u) => u.status === 'active')
              .map((u) => ({ value: u.id, label: u.display_name }))}
          />
          <TextInput
            label="Expires on"
            value={expires}
            onChange={setExpires}
            placeholder="2031-04-18"
            description="Passports and visas only. Warned about before a trip."
          />
        </Stack>
      </Card>

      <Stack direction="vertical" gap={2}>
        {progress !== null && (
          <ProgressBar
            label="Uploading"
            value={progress}
            hasValueLabel
            // 100% with the request still open is a lie people read as done.
            // Past the last byte it is the server's turn, and it says so.
            formatValueLabel={(v) => (v < 100 ? `${v}%` : 'Saving…')}
          />
        )}
        <Button
          label={progress === null ? 'Upload' : 'Uploading…'}
          variant="primary"
          width="100%"
          isDisabled={!ready}
          isLoading={progress !== null}
          onClick={upload}
        />
        <Button
          label="Cancel"
          variant="ghost"
          width="100%"
          isDisabled={progress !== null}
          onClick={() => navigate({ to: '/manage/documents' })}
        />
      </Stack>
    </Stack>
  )
}
